import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { Point } from "pixi.js";
import gsap from "gsap";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { addToTitanSpineSlot, delayFor, framesToSeconds, getDuration, resetTitanSpine, SpineComplete, SpineEvent, updateResponsiveSpineSlotsChildren } from "../../../../../utils/SpineUtils";
import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { SeededRandom } from "../../../../../utils/SeededRandom";
import { GameHungryHenrySoundEvents } from "../../../../../controller/event/GameSountEvent";
export const ALL_BOX_DIRECTIONS = ["left", "middle", "right"];
const BALL_LAND_POSITIONS = [
    [0], // first ball, always lands central
    [-107, +107], // two balls are side-by-side
    [-214, 0, +214] // three balls one central and one on each side
];
export class FeaturePurpleFish extends AbstractGameView {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
        this.pickedDirection = "middle";
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(FeaturePurpleFishEvents.FEATURE_PRESENT, this.featurePresent);
        this.registerGame(FeaturePurpleFishEvents.FEATURE_START, this.featureStart);
        this.registerGame(FeaturePurpleFishEvents.REVEAL_PRIZES, this.revealPrizes);
        this.registerGame(FeaturePurpleFishEvents.CLEAR, this.clear);
    }
    create() {
        super.create();
        this.interactive = true;
        this.seededPrizePicker = new SeededRandom();
        TitanSprite.createSprite(this, "background", this.landscapeProperties.background, this.portraitProperties.background);
        this.cat = TitanSpine.createSpine(this, "cat", this.landscapeProperties.cat, this.portraitProperties.cat);
        this.cat.getSpine(OrientationType.LANDSCAPE).state.data.defaultMix = 0.1;
        this.cat.getSpine(OrientationType.PORTRAIT).state.data.defaultMix = 0.1;
        this.catSpaceContainer = ViewUtils.createContainer(this, "cat-space-container");
        this.handleResize();
        this.pickedPrizeTextContainer = ViewUtils.createContainer(this.catSpaceContainer, "picked-prize-text-container");
        this.pickedPrizeText = TitanText.createField(this.pickedPrizeTextContainer, "picked-prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        this.missedPrizeText0 = TitanText.createField(null, "picked-prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        this.missedPrizeText1 = TitanText.createField(null, "picked-prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        this.boxLeft = TitanSpine.createSpine(this, "boxLeft", this.landscapeProperties.boxLeft, this.portraitProperties.boxLeft);
        this.boxMiddle = TitanSpine.createSpine(this, "boxMiddle", this.landscapeProperties.boxMiddle, this.portraitProperties.boxMiddle);
        this.boxRight = TitanSpine.createSpine(this, "boxRight", this.landscapeProperties.boxRight, this.portraitProperties.boxRight);
        this.allBoxes = [this.boxLeft, this.boxMiddle, this.boxRight];
        this.boxesByDirection = { left: this.boxLeft, middle: this.boxMiddle, right: this.boxRight };
        this.allBoxes.forEach((box, index) => {
            MouseUtil.addMouseDown(box, () => {
                this.dispatchGame(new EventObject(FeaturePurpleFishEvents.PICK_SELECTED.getType(), { index }));
            }, null);
        });
        this.boxItems = this.landscapeProperties.boxItems.map((boxItem, index) => {
            const item = TitanSprite.createSprite(this, `box-item-${index}`, boxItem, this.portraitProperties.boxItems[index]);
            return item;
        });
        this.furballTextContainers = [
            ViewUtils.createContainer(this.catSpaceContainer, "furball-0-text-container"),
            ViewUtils.createContainer(this.catSpaceContainer, "furball-1-text-container"),
            ViewUtils.createContainer(this.catSpaceContainer, "furball-2-text-container"),
        ];
        this.furballs = [
            TitanSpine.createSpine(this.catSpaceContainer, "furball-0", this.landscapeProperties.furball, this.portraitProperties.furball),
            TitanSpine.createSpine(this.catSpaceContainer, "furball-1", this.landscapeProperties.furball, this.portraitProperties.furball),
            TitanSpine.createSpine(this.catSpaceContainer, "furball-2", this.landscapeProperties.furball, this.portraitProperties.furball),
        ];
        this.furballText = [
            TitanText.createField(this.furballTextContainers[0], "furball-0-text", this.landscapeProperties.furballText, this.portraitProperties.furballText),
            TitanText.createField(this.furballTextContainers[1], "furball-1-text", this.landscapeProperties.furballText, this.portraitProperties.furballText),
            TitanText.createField(this.furballTextContainers[2], "furball-2-text", this.landscapeProperties.furballText, this.portraitProperties.furballText),
        ];
        this.furballs.forEach(container => container.alpha = 0);
        this.furballText.forEach((text, i) => text.setText("9", true));
        this.furballTextContainers.forEach((container, i) => addToTitanSpineSlot(this.furballs[i], container, "dynamic-value"));
        this.furballTimings = {
            "active-bounce": getDuration(this.furballs[0], "active-bounce"),
            "active-land": getDuration(this.furballs[0], "active-land"),
            "active-land-shadow": getDuration(this.furballs[0], "active-land-shadow"),
            "react-bounce": getDuration(this.furballs[0], "react-bounce"),
        };
        this.visible = false;
        this.titleText = TitanText.createField(this, "title-text", this.landscapeProperties.titleText, this.portraitProperties.titleText);
        this.helpText = TitanText.createField(this, "help-text", this.landscapeProperties.helpText, this.portraitProperties.helpText);
        this.logo = TitanSprite.createSprite(this, "logo", this.landscapeProperties.logoProperties, this.portraitProperties?.logoProperties);
        this.helpButton = this.addChild(this.factory.createBonusHelp());
    }
    featurePresent() {
        this.seededPrizePicker.seed(this.model.getGameInstanceId());
        this.helpButton.setEnabled(!Service.isReplay());
        this.helpText.alpha = 0;
        this.visible = true;
        this.cat.play("idle", true, false, 0);
        this.allBoxes.forEach(box => box.alpha = 0);
        this.boxItems.forEach(b => b.parent?.removeChild(b));
        this.missedPrizeText0.parent?.removeChild(this.missedPrizeText0);
        this.missedPrizeText1.parent?.removeChild(this.missedPrizeText1);
        this.alpha = 1;
    }
    async featureStart() {
        const boxRippleDuration = 0.5;
        this.dispatchGame(GameHungryHenrySoundEvents.BOX1_IN);
        this.playBoxIntro("left");
        await delayFor(boxRippleDuration);
        this.dispatchGame(GameHungryHenrySoundEvents.BOX2_IN);
        this.playBoxIntro("middle");
        await delayFor(boxRippleDuration);
        this.dispatchGame(GameHungryHenrySoundEvents.BOX3_IN);
        this.playBoxIntro("right");
        await SpineComplete(this.boxRight);
        this.allBoxes.forEach(box => MouseUtil.setInteractive(box, true));
        gsap.to(this.helpText, {
            alpha: 1,
            duration: 0.5,
            delay: 5,
        });
        this.dispatchGame(FeaturePurpleFishEvents.PICKS_READY);
    }
    playBoxIntro(direction) {
        const box = this.boxesByDirection[direction];
        box.alpha = 1;
        box.play(`entry_${direction}`);
        box.getSpine().update(1 / 60);
    }
    async revealPrizes(event) {
        gsap.killTweensOf(this.helpText);
        this.helpText.alpha = 0;
        this.allBoxes.forEach((box) => {
            MouseUtil.setInteractive(box, false);
        });
        this.helpButton.setEnabled(false);
        const payload = event.getPayload();
        const pickedIndex = payload["index"];
        const missedValues = payload["missedValues"];
        this.missedPrizeText0.setText(this.formatCurrency(missedValues[0]), true);
        this.missedPrizeText1.setText(this.formatCurrency(missedValues[1]), true);
        const unpickedDirections = ALL_BOX_DIRECTIONS.filter((_, index) => index !== pickedIndex);
        this.pickedDirection = ALL_BOX_DIRECTIONS[pickedIndex];
        await this.removeUnpickedBoxes(unpickedDirections);
        await delayFor(0.5);
        await this.movePickedBoxToMiddle();
        await this.revealPickedPrize();
        await delayFor(0.25);
        await this.catEatPickedPrize();
        await delayFor(0.5);
        await this.catBurpPickedPrizeValue();
        await this.revealMultiplierDigits();
        await delayFor(0.5);
        await this.combineMultiplierDigits();
        await delayFor(1);
        await this.combineMultiplierWithCashValue();
        this.dispatchGame(FeaturePurpleFishEvents.REVEAL_PRIZES_COMPLETE);
    }
    async removeUnpickedBoxes(unpickedDirections) {
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[0]], this.seededPrizePicker.pickFromArray(this.boxItems.filter(b => !b.parent)), "dynamic-item");
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[1]], this.seededPrizePicker.pickFromArray(this.boxItems.filter(b => !b.parent)), "dynamic-item");
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[0]], this.missedPrizeText0, "dynamic-item");
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[1]], this.missedPrizeText1, "dynamic-item");
        this.dispatchGame(GameHungryHenrySoundEvents.BOX_OUT);
        await Promise.all(unpickedDirections.map((direction) => {
            const box = this.boxesByDirection[direction];
            box.play(`${direction}_poof`);
            box.getSpine().update(1 / 60);
            return SpineComplete(box);
        }));
    }
    async movePickedBoxToMiddle() {
        const shouldMove = (this.pickedDirection !== "middle");
        const box = this.boxesByDirection[this.pickedDirection];
        if (shouldMove) {
            box.play(`${this.pickedDirection}_to_middle`);
            box.getSpine().update(1 / 60);
            await SpineComplete(box);
            await delayFor(1);
        }
    }
    async revealPickedPrize() {
        this.dispatchGame(GameHungryHenrySoundEvents.BOX_REVEAL);
        const box = this.boxesByDirection[this.pickedDirection];
        addToTitanSpineSlot(box, this.seededPrizePicker.pickFromArray(this.boxItems.filter(b => !b.parent)), "dynamic-item");
        box.play(`auction_middle_open_item`, false, false, 0);
        await SpineComplete(box);
    }
    async catEatPickedPrize() {
        this.cat.play("eat", false, false, 0);
        this.dispatchGame(GameHungryHenrySoundEvents.SLAM);
        await SpineEvent(this.cat, "eat_item");
        this.allBoxes.forEach((box, index) => {
            box.alpha = 0;
        });
        await SpineComplete(this.cat);
        this.cat.play("idle", true, false, 0);
    }
    async catBurpPickedPrizeValue() {
        gsap.delayedCall(0.2, () => {
            this.dispatchGame(GameHungryHenrySoundEvents.BURP);
        });
        this.cat.play("burp", false, false, 0);
        SpineComplete(this.cat).then(() => this.cat.play("idle", true, false, 0));
        await SpineEvent(this.cat, "burp_value");
        await this.onCatBurpedValue();
    }
    async onCatBurpedValue() {
        const data = this.model.getPurpleFishChanceGameData();
        const value = data.getInitialPickValue();
        const startLocation = new Point(-293, -355);
        const endLocation = new Point(0, -440);
        this.pickedPrizeText.setText(this.formatCurrency(value), true);
        this.pickedPrizeTextContainer.scale.set(0.7);
        this.pickedPrizeTextContainer.position.copyFrom(startLocation);
        return gsap.timeline()
            .to(this.pickedPrizeTextContainer, { pixi: { scale: 1 }, duration: 1.5, ease: "back.out(4)" }, 0)
            .add(gsap.to(this.pickedPrizeTextContainer, {
            duration: 3,
            ease: "power2.out",
            motionPath: {
                path: [
                    startLocation,
                    {
                        x: -373,
                        y: -458
                    },
                    endLocation
                ],
                curviness: 2,
                fromCurrent: false,
            }
        }), 0)
            .play();
    }
    async revealMultiplierDigits() {
        const data = this.model.getPurpleFishChanceGameData();
        const bubblesData = data.getMultiplierBubbles();
        const finalMultiplier = bubblesData[bubblesData.length - 1].value;
        console.log("Final multiplier: ", finalMultiplier);
        let runningFurballIndices = [];
        for (const [index, bubble] of bubblesData.entries()) {
            this.cat.play("hairball", false, false, 0);
            this.dispatchGame(GameHungryHenrySoundEvents.FURBALL);
            SpineComplete(this.cat).then(() => {
                if (this.cat.getTrackEntry(0).trackTime >= this.cat.getTrackEntry(0).trackEnd) {
                    this.cat.play("idle", true, false, 0);
                }
            });
            await SpineEvent(this.cat, "furball");
            const furball = this.furballs[index];
            furball.parent.addChildAt(furball, index);
            this.furballText[index].setText(bubble.newValue.toFixed(0), true);
            const timeline = gsap.timeline();
            runningFurballIndices.splice(bubble.position, 0, index);
            runningFurballIndices.forEach((runningFurballPositionIndex, position) => {
                if (runningFurballPositionIndex === index) { // is the new ball, we handle it's motion separately
                    return;
                }
                timeline.to(this.furballs[runningFurballPositionIndex], {
                    x: BALL_LAND_POSITIONS[index][position],
                    duration: 0.5,
                    ease: "expo.out",
                    delay: index < 2 ? 0.2 : Math.abs(bubble.position - position) * 0.2,
                    onStart: () => {
                        this.getHitReactionBounceTween(this.furballs[runningFurballPositionIndex]).play();
                    }
                }, 0);
            });
            await this.getFurballDropToCenterTween(furball, index, bubble.position);
            await timeline.play();
            await delayFor(0.5);
        }
    }
    async getFurballDropToCenterTween(furball, index, position) {
        furball.alpha = 1;
        furball.scale.set(0.78, 0.73);
        furball.position.set(0, 153);
        if (index === 0) {
            await gsap.to(furball, {
                pixi: { x: BALL_LAND_POSITIONS[index][position], y: 305, scale: 1 },
                duration: 0.233,
                ease: "linear"
            });
            furball.play("active-land", false, false, 1);
            furball.play("active-land-shadow", false, false, 0);
        }
        else {
            const timelineX = gsap.timeline();
            timelineX.to(furball, {
                pixi: { positionX: BALL_LAND_POSITIONS[index][position], scale: 1 },
                duration: 0.8,
                ease: "sine.in"
            });
            const timelineY = gsap.timeline();
            timelineY.to(furball, {
                pixi: { positionY: 160 },
                duration: 0.066667,
                ease: "sine.out",
            });
            timelineY.to(furball, {
                pixi: { positionY: 115 },
                duration: 0.3,
                ease: "sine.inOut",
                onStart: () => furball.play("active-bounce", false, false, 1)
            });
            timelineY.to(furball, {
                pixi: { positionY: 305 },
                duration: 0.3,
                ease: "sine.in",
            });
            const timeline = gsap.timeline();
            timeline.add(timelineX, 0);
            timeline.add(timelineY, 0);
            await timeline.play();
            furball.play("active-land", false, false, 1);
            furball.play("active-land-shadow", false, false, 0);
        }
    }
    async combineMultiplierDigits() {
        const data = this.model.getPurpleFishChanceGameData();
        const bubblesData = data.getMultiplierBubbles();
        const finalMultiplier = bubblesData[bubblesData.length - 1].value;
        const timeline = gsap.timeline();
        this.furballs[0].parent.setChildIndex(this.furballs[0], this.furballs.length - 1);
        this.furballs.forEach((furball, i) => {
            timeline
                .to(furball, {
                pixi: { x: 0 },
                duration: 0.5,
                ease: "expo.out"
            }, 0)
                .add(() => furball.play("active-bounce", false, false, 1), "-=0.1")
                .to(this.furballText[i], {
                pixi: { alpha: 0 },
                duration: 0.5,
                ease: "expo.in",
            }, 0)
                .add(() => {
                furball.alpha = i === 0 ? 1 : 0;
            }, "-=0.1");
        });
        timeline
            .add(() => {
            this.dispatchGame(GameHungryHenrySoundEvents.COMBINE);
            this.furballText[0].setText(finalMultiplier.toFixed(0), true);
            this.furballText[0].alpha = 1;
            this.furballTextContainers[0].scale.set(0);
        })
            .to(this.furballTextContainers[0], {
            pixi: { scale: 1.2 },
            duration: 0.3,
            ease: "back.out(1.7)",
        })
            .to(this.furballTextContainers[0], {
            pixi: { scale: 1 },
            duration: 0.7,
            ease: "power1.in",
        });
        return timeline.play();
    }
    async combineMultiplierWithCashValue() {
        const data = this.model.getPurpleFishChanceGameData();
        const bubblesData = data.getMultiplierBubbles();
        const finalMultiplier = bubblesData[bubblesData.length - 1].value;
        const cashValue = this.formatCurrency(data.getInitialPickValue());
        const rollup = { val: data.getInitialPickValue() };
        const timeline = gsap.timeline();
        gsap.delayedCall(0.2, () => this.dispatchGame(GameHungryHenrySoundEvents.MULTIPLY));
        timeline.to(this.furballs[0], {
            pixi: { x: this.pickedPrizeTextContainer.x, y: this.pickedPrizeTextContainer.y },
            duration: 1.333,
            ease: "elastic.inOut(1,0.75)"
        }, 0);
        timeline.add(() => this.furballs[0].play("fly-up-shadow", false, false, 0), 0.2);
        timeline.to(this.furballText[0], {
            pixi: { alpha: 0 },
            duration: 1.33,
            ease: "expo.in"
        }, 0);
        timeline.to(this.pickedPrizeTextContainer, {
            pixi: { alpha: 0 },
            duration: 1.33,
            ease: "expo.in"
        }, 0);
        timeline.add(() => this.cat.play("happy", true, false, 0));
        timeline.add(() => {
            this.furballText[0].alpha = 1;
            this.furballText[0].setText(`${cashValue}\nX\n${finalMultiplier.toFixed(0)}`, true);
        });
        timeline.to(this.furballTextContainers[0], {
            pixi: { scale: 1.2 },
            duration: 0.5,
            ease: "power1.out",
        });
        timeline.to(this.furballTextContainers[0], {
            pixi: { scale: 1 },
            duration: 0.5,
            ease: "power1.in",
        });
        timeline.to({}, { duration: 2 });
        timeline.to(this.furballTextContainers[0], {
            pixi: { scale: 0 },
            duration: 0.333,
            ease: "power1.out",
        });
        timeline.to(rollup, {
            val: data.getTotalWin(),
            duration: 0.5,
            ease: "none",
            onStart: () => this.dispatchGame(GameHungryHenrySoundEvents.TWEEN_UP),
            onUpdate: () => {
                this.furballText[0].setText(this.formatCurrency(rollup.val), true);
            }
        });
        timeline.to(this.furballTextContainers[0], {
            pixi: { scale: 1.2 },
            duration: 0.5,
            ease: "back.out(1.7)",
        }, "<");
        timeline.to(this.furballTextContainers[0], {
            pixi: { scale: 1 },
            duration: 0.5,
            ease: "power1.in",
        });
        return timeline.play();
    }
    getHitReactionBounceTween(furball) {
        const startPos = furball.y;
        const timeline = gsap.timeline();
        furball.play("react-bounce", false, false, 1);
        timeline.to(furball, {
            pixi: { y: startPos - 30 },
            duration: framesToSeconds(7),
            ease: "sine.out",
            delay: framesToSeconds(1)
        });
        timeline.to(furball, {
            pixi: { y: startPos },
            duration: framesToSeconds(7),
            ease: "sine.in"
        });
        return timeline;
    }
    async clear() {
        this.visible = false;
        this.alpha = 1;
        this.pickedPrizeTextContainer.alpha = 1;
        this.pickedPrizeText.setText("", true);
        this.pickedPrizeText.alpha = 1;
        this.furballText.forEach(t => {
            t.setText("", true);
            t.alpha = 1;
        });
        this.furballs.forEach(c => {
            c.alpha = 0;
            resetTitanSpine(c);
        });
        this.boxItems.forEach(item => item.parent?.removeChild(item));
    }
    handleRotate(event) {
        super.handleRotate(event);
        updateResponsiveSpineSlotsChildren(this.cat);
        this.furballs.forEach(furball => updateResponsiveSpineSlotsChildren(furball));
        [this.boxLeft, this.boxMiddle, this.boxRight].forEach(box => updateResponsiveSpineSlotsChildren(box));
    }
    handleResize(event) {
        super.handleResize(event);
        this.catSpaceContainer.position.set(this.cat.x, this.cat.y);
        this.catSpaceContainer.scale.set(this.cat.scale.x, this.cat.scale.y);
    }
}
FeaturePurpleFish.REPLAY_PICK_INDEX = 1;
export class FeaturePurpleFishEvents {
}
FeaturePurpleFishEvents.FEATURE_PRESENT = new EventObject("FeaturePurpleFishEvents.FEATURE_PRESENT");
FeaturePurpleFishEvents.FEATURE_START = new EventObject("FeaturePurpleFishEvents.FEATURE_START");
FeaturePurpleFishEvents.PICKS_READY = new EventObject("FeaturePurpleFishEvents.PICKS_READY");
FeaturePurpleFishEvents.PICK_SELECTED = new EventObject("FeaturePurpleFishEvents.PICK_SELECTED");
FeaturePurpleFishEvents.REVEAL_PRIZES = new EventObject("FeaturePurpleFishEvents.REVEAL_PRIZES");
FeaturePurpleFishEvents.REVEAL_PRIZES_COMPLETE = new EventObject("FeaturePurpleFishEvents.REVEAL_PRIZES_COMPLETE");
FeaturePurpleFishEvents.CLEAR = new EventObject("FeaturePurpleFishEvents.CLEAR");
//# sourceMappingURL=FeaturePurpleFish.js.map